// src/pages/FilesPage.tsx

import { useRef, useState } from 'react';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useAuth } from '../hooks/useAuth';
import { uploadFile as uploadFileToStorage } from '../utils/uploadUtils';
import {
  useGetFilesQuery,
  useGetReceivedFilesQuery,
  useUpdateFileMutation,
  useDeleteFileMutation,
  useShareFileMutation,
} from '../api/filesApi';
import { useGetContactsQuery } from '../api/contactsApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import { MaterialsLibrary } from '../components/materials/MaterialsLibrary';
import Button from '../components/common/Button';
import FileDetailSlideOver from '../components/files/FileDetailSlideOver';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import type { FileAsset, ReceivedFile } from '../types/api';
import { useHasCapability } from '../services/centralContext';
import { FEATURE_CAPABILITIES } from '../config/featureCapabilities';

// 'materials' is a FEDERATED view of PitchLess artifacts, not CRM files.
// It sits beside the file tabs rather than inside them because the two have
// different lifecycles: a material cannot be renamed, re-shared or deleted
// here, and CRM stores no row for one. See MaterialsLibrary.
type TabType = 'files' | 'received' | 'materials';

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getFileIcon(mimeType: string | null): string {
  if (!mimeType) return 'document';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  return 'document';
}

export default function FilesPage() {
  // Replaced with useActiveWorkspace
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [activeTab, setActiveTab] = useState<TabType>('files');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Uploading is a WORKSPACE-level authority question — there is no existing
  // file whose `access` flags could answer it — so it comes from the global
  // capability, matching the server gate on POST /files and GET
  // /files/upload-url. A viewer holds files.read=true and files.write=false.
  //
  // The server remains authoritative; this only stops the UI offering an
  // action the backend will refuse with 403.
  const canUpload = useHasCapability(
    FEATURE_CAPABILITIES.uploadFile.domain,
    FEATURE_CAPABILITIES.uploadFile.action,
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileAsset | null>(null);
  const [selectedFileIdForDetail, setSelectedFileIdForDetail] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fileToDelete, setFileToDelete] = useState<FileAsset | null>(null);

  const {
    data: files,
    isLoading: filesLoading,
    error: filesError,
    refetch: refetchFiles,
  } = useGetFilesQuery({ workspaceId, q: searchQuery || undefined }, { skip: !workspaceId });

  const {
    data: receivedFiles,
    isLoading: receivedLoading,
    error: receivedError,
    refetch: refetchReceived,
  } = useGetReceivedFilesQuery({ workspaceId }, { skip: !workspaceId });

  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const [updateFile] = useUpdateFileMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [shareFile, { isLoading: isSharing }] = useShareFileMutation();

  // The modal used to ask for a STORAGE KEY: the caller typed the key of an
  // object they had uploaded through some other channel, and this page merely
  // registered a row pointing at it. Nothing here ever moved bytes, so there
  // was no way to upload a file from the Files page at all — and a typo
  // produced a record pointing at nothing. It now picks a real file and runs
  // the presign -> PUT -> register pipeline `utils/uploadUtils` already
  // provides (the same one Tasks and the composer use, and the same one
  // mobile's Upload File sheet runs).
  const { token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedUpload, setSelectedUpload] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    originalName: '',
    description: '',
    contactId: '',
  });

  const [shareFormData, setShareFormData] = useState({
    toUserId: '',
    toContactId: '',
    message: '',
  });

  const resetForm = () => {
    setFormData({
      originalName: '',
      description: '',
      contactId: '',
    });
    setSelectedUpload(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedUpload(file);
    setUploadError(null);
    // Seed the name from the file, but never overwrite one already typed.
    if (file && !formData.originalName.trim()) {
      setFormData((prev) => ({ ...prev, originalName: file.name }));
    }
  };

  const resetShareForm = () => {
    setShareFormData({
      toUserId: '',
      toContactId: '',
      message: '',
    });
    setSelectedFile(null);
  };

  const handleCreateFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId) return;
    if (!selectedUpload) {
      setUploadError('Choose a file to upload.');
      return;
    }
    if (!token) {
      setUploadError('Not authenticated — cannot upload.');
      return;
    }

    setIsCreating(true);
    setUploadError(null);
    try {
      await uploadFileToStorage(selectedUpload, token, {
        description: formData.description || undefined,
        contactId: formData.contactId || undefined,
        originalName: formData.originalName.trim() || undefined,
      });
      // The upload is a plain fetch, so the files cache does not know about it.
      await refetchFiles();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleShareFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    try {
      await shareFile({
        id: selectedFile.id,
        toUserId: shareFormData.toUserId || undefined,
        toContactId: shareFormData.toContactId || undefined,
        message: shareFormData.message || undefined,
      }).unwrap();
      setShowShareModal(false);
      resetShareForm();
    } catch (error) {
      console.error('Failed to share file:', error);
    }
  };

  const handleDeleteClick = (file: FileAsset) => {
    setFileToDelete(file);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id).unwrap();
      setFileToDelete(null);
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const openShareModal = (file: FileAsset) => {
    setSelectedFile(file);
    setShowShareModal(true);
  };

  // The Materials tab is a DIFFERENT data source (PitchLess, via
  // MaterialsLibrary) and must not inherit the file queries' states. Without
  // the `activeTab === 'materials'` guards below, a slow or failing
  // received-files query would blank the whole page and the Materials tab
  // would be unreachable — a failure in one system hiding another.
  const isMaterials = activeTab === 'materials';
  const isLoading = isMaterials ? false : activeTab === 'files' ? filesLoading : receivedLoading;
  const error = isMaterials ? undefined : activeTab === 'files' ? filesError : receivedError;
  const refetch = activeTab === 'files' ? refetchFiles : refetchReceived;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load files." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view files.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Files</h1>
          <p className="text-sm text-gray-500">
            {activeTab === 'files'
              ? `${files?.length || 0} file${files?.length !== 1 ? 's' : ''}`
              : `${receivedFiles?.length || 0} received file${receivedFiles?.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {canUpload && (
          <Button onClick={() => setShowCreateModal(true)}>Upload File</Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('files')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'files'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Files
          </button>
          <button
            onClick={() => setActiveTab('received')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'received'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Received
          </button>
          <button
            onClick={() => setActiveTab('materials')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'materials'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Materials
          </button>
        </nav>
      </div>

      {/* Search */}
      {activeTab === 'files' && (
        <div className="relative">
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full max-w-md px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg
            className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      )}

      {/* PitchLess materials. Rendered before the file content so the
          file branches below stay a single untouched expression. */}
      {activeTab === 'materials' && <MaterialsLibrary />}

      {/* Content */}
      {activeTab !== 'materials' && (activeTab === 'files' ? (
        files?.length === 0 ? (
          <EmptyFilesState
            onUpload={canUpload ? () => setShowCreateModal(true) : undefined}
          />
        ) : (
          <FilesGrid
            files={files || []}
            onShare={openShareModal}
            onDelete={handleDeleteClick}
            onSelect={(id) => setSelectedFileIdForDetail(id)}
          />
        )
      ) : receivedFiles?.length === 0 ? (
        <EmptyReceivedState />
      ) : (
        <ReceivedFilesList files={receivedFiles || []} />
      ))}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload File</h2>
            <form onSubmit={handleCreateFile} className="space-y-4">
              {/* The real file, in place of the storage key nobody could know. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChosen}
                  className="hidden"
                />
                {selectedUpload ? (
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {selectedUpload.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(selectedUpload.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full rounded-lg border border-dashed border-gray-300 bg-gray-50 py-5 text-center hover:border-blue-400 hover:bg-blue-50"
                  >
                    <span className="block text-sm font-semibold text-blue-600">Choose File</span>
                    <span className="mt-1 block text-xs text-gray-500">
                      Pick a file from this computer
                    </span>
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Name *
                </label>
                <input
                  type="text"
                  value={formData.originalName}
                  onChange={(e) => setFormData({ ...formData, originalName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Contact
                </label>
                <select
                  value={formData.contactId}
                  onChange={(e) => setFormData({ ...formData, contactId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">None</option>
                  {contacts?.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {uploadError && (
                <p className="text-sm text-red-600" role="alert">
                  {uploadError}
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isCreating}
                  disabled={!selectedUpload || isCreating}
                  className="flex-1"
                >
                  Upload
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedFile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Share "{selectedFile.originalName}"
            </h2>
            <form onSubmit={handleShareFile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share with Contact
                </label>
                <select
                  value={shareFormData.toContactId}
                  onChange={(e) =>
                    setShareFormData({ ...shareFormData, toContactId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a contact</option>
                  {contacts?.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message (optional)
                </label>
                <textarea
                  value={shareFormData.message}
                  onChange={(e) =>
                    setShareFormData({ ...shareFormData, message: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add a message..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowShareModal(false);
                    resetShareForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={isSharing} className="flex-1">
                  Share
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* File Detail Slide-Over */}
      <FileDetailSlideOver
        fileId={selectedFileIdForDetail}
        onClose={() => setSelectedFileIdForDetail(null)}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={!!fileToDelete}
        itemName={fileToDelete?.originalName}
        itemType="file"
        onConfirm={handleConfirmDelete}
        onCancel={() => setFileToDelete(null)}
      />
    </div>
  );
}

// `onUpload` is optional: a principal without crm.files.write gets the empty
// state without a call to action it cannot complete.
function EmptyFilesState({ onUpload }: { onUpload?: () => void }) {
  return (
    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Files Yet</h3>
      <p className="text-gray-500 mb-4">
        {onUpload
          ? 'Upload your first file to get started.'
          : 'No files have been shared with you yet.'}
      </p>
      {onUpload && <Button onClick={onUpload}>Upload File</Button>}
    </div>
  );
}

function EmptyReceivedState() {
  return (
    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Received Files</h3>
      <p className="text-gray-500">Files shared with you will appear here.</p>
    </div>
  );
}

interface FilesGridProps {
  files: FileAsset[];
  onShare: (file: FileAsset) => void;
  onDelete: (file: FileAsset) => void;
  onSelect: (id: string) => void;
}

function FilesGrid({ files, onShare, onDelete, onSelect }: FilesGridProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {files.map((file) => (
        <FileCard
          key={file.id}
          file={file}
          onShare={() => onShare(file)}
          onDelete={() => onDelete(file)}
          onClick={() => onSelect(file.id)}
        />
      ))}
    </div>
  );
}

interface FileCardProps {
  file: FileAsset;
  onShare: () => void;
  onDelete: () => void;
  onClick: () => void;
}

function FileCard({ file, onShare, onDelete, onClick }: FileCardProps) {
  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{file.originalName}</h3>
          <p className="text-xs text-gray-500 mt-1">{formatFileSize(file.sizeBytes)}</p>
          {file.description && (
            <p className="text-xs text-gray-500 mt-1 truncate">{file.description}</p>
          )}
          {file.contact && (
            <p className="text-xs text-blue-600 mt-1 truncate">Contact: {file.contact.fullName}</p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-500">
          {new Date(file.createdAt).toLocaleDateString()}
        </span>
        {/*
          C4.5 R3 — controls follow the SERVER's per-file authority.

          These used to render unconditionally, which was already untruthful
          (a viewer saw Delete) and became worse once R3 let a grant-only file
          appear in this list: the backend correctly refuses to mutate it, so
          offering the button promised something impossible.

          FAIL CLOSED: `access` absent or malformed hides the controls. We do
          NOT fall back to a global files.write/files.delete capability —
          global capability says nothing about THIS file, which is exactly the
          mistake being corrected. Hiding a button is an affordance, not an
          access control; the backend still denies independently.
        */}
        <div className="flex gap-2">
          {file.access?.canShare === true && (
            <button
              onClick={(e) => { e.stopPropagation(); onShare(); }}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Share
            </button>
          )}
          {file.access?.canDelete === true && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-xs text-red-600 hover:text-red-700"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ReceivedFilesListProps {
  files: ReceivedFile[];
}

function ReceivedFilesList({ files }: ReceivedFilesListProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                File
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                From
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                Message
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Received
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {files.map((received) => (
              <tr key={received.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{received.file.originalName}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(received.file.sizeBytes)}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {received.fromUser.fullName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                  {received.message || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {new Date(received.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
